/**
 * Synchronous "does this browser have a Convex session?" hint.
 *
 * Why this exists
 * ---------------
 * `useConvexAuth().isAuthenticated` is NOT a network answer. In
 * `@convex-dev/auth`, `isAuthenticated` is literally `tokenState !== null`,
 * and `tokenState` is seeded from `localStorage["__convexAuthJWT"]` on mount.
 * The WebSocket only *confirms* the cached token afterwards
 * (AuthenticationManager `waitingForServerConfirmationOfCachedToken`).
 *
 * So the browser can know, synchronously and for free, whether to paint the
 * sign-in card or wait for the shell — with no flash for signed-in users,
 * because the token is already in localStorage before the first render.
 *
 * Why it is only a hint, never a source of truth
 * ----------------------------------------------
 * A stored token can be expired or revoked, so this must never be used to
 * grant anything. It only decides what to DRAW during the first ~100ms. The
 * real gate is still `useConvexAuth`; if the token turns out bad, the shell is
 * never shown and AuthGate falls through to sign-in as before.
 */

const TOKEN_KEY = "__convexAuthJWT";

/**
 * ConvexAuthProvider namespaces keys as `${key}_${namespace}` with
 * non-alphanumerics stripped. The default namespace is NOT "convex": it is the
 * client's deployment URL (`storageNamespace ?? client.address`), so the real
 * key looks like `__convexAuthJWT_httpsconvexpreviewapiyatisharacom`. The old
 * hint only checked `__convexAuthJWT_convex`, never matched, and so reported
 * "anonymous" for every signed-in browser.
 */
function escapedNamespace(namespace: string): string {
  return namespace.replace(/[^a-zA-Z0-9]/g, "");
}

function defaultNamespace(): string {
  return process.env.NEXT_PUBLIC_CONVEX_URL ?? "";
}

const REFRESH_KEY = "__convexAuthRefreshToken";

/** The exact storage key ConvexAuthProvider uses for this deployment. */
export function convexAuthStorageKey(base: string, namespace = defaultNamespace()): string {
  return namespace ? `${base}_${escapedNamespace(namespace)}` : "";
}

/**
 * Only THIS deployment's key counts. A prefix scan used to accept any
 * `__convexAuthJWT*` key, so a stale token left under another namespace (an
 * older Convex URL, a different deployment on the same origin) kept the hint
 * "signed in" forever: the boot frame showed after sign-out, and AuthGate —
 * which waited on the hint — drew nothing once auth settled anonymous.
 * The scan survives only as a fallback when the namespace is unknown.
 */
function readStoredToken(storage: Storage, namespace: string): string | null {
  const exact = convexAuthStorageKey(TOKEN_KEY, namespace);
  if (exact) {
    try {
      const value = storage.getItem(exact);
      return typeof value === "string" && value.trim() ? value : null;
    } catch {
      return null;
    }
  }
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key || !key.startsWith(TOKEN_KEY)) continue;
      const value = storage.getItem(key);
      if (typeof value === "string" && value.trim()) return value;
    }
  } catch {
    /* storage blocked mid-scan */
  }
  return null;
}

/**
 * Remove Convex Auth token keys that belong to OTHER namespaces. They can
 * never authenticate this deployment, but they look like a session marker to
 * anything scanning storage. The current namespace's keys are left alone:
 * ConvexAuthProvider owns them (and clears them itself on sign-out).
 */
export function purgeStaleConvexSessionKeys(namespace = defaultNamespace()): void {
  if (typeof window === "undefined" || !namespace) return;
  try {
    const storage = window.localStorage;
    const keep = new Set([
      convexAuthStorageKey(TOKEN_KEY, namespace),
      convexAuthStorageKey(REFRESH_KEY, namespace),
    ]);
    const stale: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key || keep.has(key)) continue;
      if (key.startsWith(TOKEN_KEY) || key.startsWith(REFRESH_KEY)) stale.push(key);
    }
    for (const key of stale) storage.removeItem(key);
  } catch {
    /* storage blocked */
  }
}

/**
 * True when a token is present in storage. No verification — a corrupt or
 * expired value still reports "maybe signed in" and lets the real auth gate
 * make the call. The inline boot script in the root layout uses the same rule
 * (see studio-boot-frame-script.ts) to decide app frame vs sign-in before
 * first paint.
 */
export function hasStoredConvexSession(namespace = defaultNamespace()): boolean {
  if (typeof window === "undefined") return false;
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    // Blocked by privacy settings / third-party context.
    return false;
  }
  if (!storage) return false;
  return readStoredToken(storage, namespace) !== null;
}

/**
 * Last resort for a dead session whose sign-out call never returns: drop this
 * deployment's Convex Auth tokens (and stale ones) straight from storage. The
 * caller reloads afterwards, because ConvexAuthProvider keeps its own
 * in-memory copy and only re-reads storage on mount.
 */
export function clearStoredConvexSession(namespace = defaultNamespace()): void {
  if (typeof window === "undefined") return;
  try {
    const storage = window.localStorage;
    const exact = [convexAuthStorageKey(TOKEN_KEY, namespace), convexAuthStorageKey(REFRESH_KEY, namespace)];
    for (const key of exact) if (key) storage.removeItem(key);
  } catch {
    /* storage blocked */
  }
  purgeStaleConvexSessionKeys(namespace);
}
